import { useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import { ApproveRejectButtons } from 'components/Buttons';
import { LabeledTextarea, Column, Row, Label, Button, Link, Card } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { SendIcon } from 'components/Icons';

import { useProjectUpdate } from 'providers/Project';
import { useToast } from 'providers/Toast';
import { useUser } from 'hooks';

import { OutsourceDataComposed } from '.';
import { MESSAGES } from '../utils';

interface OutsourceControl {
  approved: boolean;
  justification?: string;
}

interface SingleOutsourceProps {
  outsource: OutsourceDataComposed;
}

export function SingleOutsource({ outsource }: SingleOutsourceProps) {
  const { t } = useLang();
  const { addToast } = useToast();
  const loggedUser = useUser();
  const updateProject = useProjectUpdate();

  const [outsourceControl, setOutsourceControl] = useState<OutsourceControl>(
    {} as OutsourceControl,
  );

  const hasApprovedStatus = outsourceControl.approved !== undefined;

  const canSendReview =
    hasApprovedStatus &&
    (outsourceControl.approved ? true : !!outsourceControl.justification?.trim());

  const sendAnswer = actions.projects.answerTaskOutsource.use({
    block: !canSendReview,

    params: {
      project: outsource.projectId,
      id: outsource.id,
      ...outsourceControl,
    },

    onSuccess: (result, { approved, project: projectId }) => {
      if (approved) {
        updateProject(projectId, (old) => ({
          ...old,

          services: old.services.map((oldService) =>
            oldService.id !== outsource.id
              ? oldService
              : {
                  ...oldService,
                  outsourceConfig: {
                    ...oldService.outsourceConfig!,
                    approved: true,
                    approvedAt: new Date().toISOString(),
                    approvedBy: loggedUser.id,
                  },
                },
          ),

          tasks: old.tasks.map((task) =>
            task.serviceConfigId !== outsource.id
              ? task
              : {
                  ...task,
                  ...result,
                  owner: task.owner,
                  subOwner: task.subOwner,
                  subTasks: task.subTasks,
                },
          ),
        }));
      } else {
        updateProject(projectId, (old) => ({
          ...old,
          services: old.services.filter((oldService) => oldService.id !== outsource.id),
          tasks: old.tasks.filter((task) => task.serviceConfigId !== outsource.id),
        }));
      }

      addToast(MESSAGES.outsourcingSuccess(t, outsourceControl.approved ? 'approved' : 'rejected'));
    },
  });

  const handleApproveChange = useCallback((state: boolean) => {
    setOutsourceControl((old) => ({
      ...old,
      approved: state,
    }));
  }, []);

  const handleJustificationChange = useCallback((value: string) => {
    setOutsourceControl((old) => ({
      ...old,
      justification: value,
    }));
  }, []);

  return (
    <Card flow="column" gap="6">
      <Row
        justifyContent="space-between"
        className="small_tablet:flex-col small_tablet:items-start"
      >
        <Column gap="2">
          <Row gap="2" className="small_tablet:flex-col small_tablet:items-start">
            <Link to="projectProfile" params={outsource} className="font-bold text-primary">
              {outsource.projectName}
            </Link>

            <span>{outsource.clientName}</span>
          </Row>

          <span>
            <strong>{t('service')}</strong>: {outsource.serviceName}
          </span>
        </Column>

        <Row gap="2">
          <span>{t('requested-by')}:</span>

          <UserAvatar user={outsource.owner} name nameClassName="font-bold" />
        </Row>
      </Row>

      <Row justifyContent="space-between" alignItems="flex-start" className="small_tablet:flex-col">
        <Column>
          <Row
            gap="8"
            className="small_tablet:flex-col small_tablet:items-start small_tablet:gap-2"
          >
            <Row gap="2" fullWidth={false}>
              <Label>{t('provider')}:</Label>

              <strong>{outsource.providerName}</strong>
            </Row>

            <Row gap="2" fullWidth={false}>
              <Label>{t('provider-cost')}:</Label>

              <strong>{`${outsource.providerCost.currency} ${outsource.providerCost.value}`}</strong>
            </Row>

            <Row gap="2" fullWidth={false}>
              <Label>{t('cost-to-client')}:</Label>

              <strong>{`${outsource.clientCost.currency} ${outsource.clientCost.value}`}</strong>
            </Row>
          </Row>

          <Column gap="2">
            <p className="font-semibold">{t('user-justification')}:</p>

            <p className="max-w-[560px] rounded-lg border bg-muted p-2 italic">
              {outsource?.justification}
            </p>
          </Column>
        </Column>

        <Column>
          <ApproveRejectButtons value={outsourceControl.approved} onChange={handleApproveChange} />

          {hasApprovedStatus && !outsourceControl.approved && (
            <LabeledTextarea
              label={`${t('justification')}:`}
              value={outsourceControl.justification || ''}
              onChange={({ target }) => handleJustificationChange(target.value)}
            />
          )}

          <Button type="button" {...sendAnswer.buttonProps} disabled={!canSendReview}>
            <SendIcon />
            {t('send')}
          </Button>
        </Column>
      </Row>
    </Card>
  );
}
